import { supabase, isSupabaseConfigured } from "@/lib/supabase";

function ensureLive() {
  if (!isSupabaseConfigured) {
    throw new Error("Ratio & capacity requires a live HoneyAcorn environment.");
  }
}

function ensureData(data, error) {
  if (error) throw error;
  return data || [];
}

export async function fetchRatioCapacitySnapshot(locationId = null) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_ratio_capacity_snapshot", {
    p_location_id: locationId || null,
  });
  return ensureData(data, error);
}

export async function fetchRatioCapacityForecast({ date, locationId = null } = {}) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_ratio_capacity_forecast", {
    p_date: date || null,
    p_location_id: locationId || null,
  });
  return ensureData(data, error);
}

export async function fetchRatioCapacitySetup() {
  ensureLive();
  const [
    locationsResult,
    policySetsResult,
    rulesResult,
    classroomsResult,
    assignmentsResult,
    childrenResult,
    schedulesResult,
    shiftsResult,
    breaksResult,
  ] = await Promise.all([
    supabase
      .from("locations")
      .select("id, name, code, address, active, is_default")
      .order("name", { ascending: true }),
    supabase
      .from("ratio_policy_sets")
      .select("id, location_id, name, jurisdiction_label, source_reference, effective_on, ends_on, mixed_age_mode, active")
      .order("effective_on", { ascending: false }),
    supabase
      .from("ratio_policy_rules")
      .select("id, policy_set_id, location_id, label, min_age_months, max_age_months, children_per_qualified_staff, max_group_size")
      .order("min_age_months", { ascending: true }),
    supabase
      .from("classrooms")
      .select("id, name, age_group, location_id, capacity, licensed_capacity")
      .order("name", { ascending: true }),
    supabase
      .from("staff_assignments")
      .select("id, profile_id, classroom_id, position, starts_on, ends_on, counts_toward_ratio, profile:profiles(id, full_name, email), classroom:classrooms(id, name)")
      .order("starts_on", { ascending: false }),
    supabase
      .from("children")
      .select("id, first_name, last_name, classroom_id, withdrawn_at, classroom:classrooms(id, name)")
      .is("withdrawn_at", null)
      .order("first_name", { ascending: true }),
    supabase
      .from("child_schedule_windows")
      .select("id, child_id, classroom_id, weekday, starts_at, ends_at, effective_on, ends_on, child:children(id, first_name, last_name), classroom:classrooms(id, name)")
      .order("weekday", { ascending: true })
      .order("starts_at", { ascending: true }),
    supabase
      .from("staff_shifts")
      .select("id, profile_id, classroom_id, starts_at, ends_at, profile:profiles(id, full_name), classroom:classrooms(id, name)")
      .gte("ends_at", new Date().toISOString())
      .order("starts_at", { ascending: true })
      .limit(100),
    supabase
      .from("staff_shift_breaks")
      .select("id, staff_shift_id, starts_at, ends_at")
      .order("starts_at", { ascending: true }),
  ]);

  return {
    locations: ensureData(locationsResult.data, locationsResult.error),
    policySets: ensureData(policySetsResult.data, policySetsResult.error),
    rules: ensureData(rulesResult.data, rulesResult.error),
    classrooms: ensureData(classroomsResult.data, classroomsResult.error),
    assignments: ensureData(assignmentsResult.data, assignmentsResult.error),
    children: ensureData(childrenResult.data, childrenResult.error),
    schedules: ensureData(schedulesResult.data, schedulesResult.error),
    shifts: ensureData(shiftsResult.data, shiftsResult.error),
    breaks: ensureData(breaksResult.data, breaksResult.error),
  };
}

export async function createLocation({ organizationId, name, code, address }) {
  ensureLive();
  const { data, error } = await supabase
    .from("locations")
    .insert({
      organization_id: organizationId,
      name: name.trim(),
      code: code?.trim() || null,
      address: address?.trim() || null,
    })
    .select("id, name, code, address, active, is_default")
    .single();
  if (error) throw error;
  return data;
}

export async function updateClassroomRatioConfig(id, patch) {
  ensureLive();
  const { data, error } = await supabase
    .from("classrooms")
    .update({
      location_id: patch.locationId,
      licensed_capacity:
        patch.licensedCapacity === "" || patch.licensedCapacity == null
          ? null
          : Number(patch.licensedCapacity),
    })
    .eq("id", id)
    .select("id, location_id, licensed_capacity")
    .single();
  if (error) throw error;
  return data;
}

export async function createRatioPolicySet({
  organizationId,
  locationId,
  name,
  jurisdictionLabel,
  sourceReference,
  effectiveOn,
  mixedAgeMode,
  actorProfileId,
}) {
  ensureLive();
  const { data, error } = await supabase
    .from("ratio_policy_sets")
    .insert({
      organization_id: organizationId,
      location_id: locationId,
      name: name.trim(),
      jurisdiction_label: jurisdictionLabel?.trim() || null,
      source_reference: sourceReference?.trim() || null,
      effective_on: effectiveOn,
      mixed_age_mode: mixedAgeMode,
      created_by: actorProfileId || null,
    })
    .select("id, location_id, name, jurisdiction_label, source_reference, effective_on, ends_on, mixed_age_mode, active")
    .single();
  if (error) throw error;
  return data;
}

export async function setRatioPolicyActive(id, active) {
  ensureLive();
  const { data, error } = await supabase
    .from("ratio_policy_sets")
    .update({ active: Boolean(active) })
    .eq("id", id)
    .select("id, active")
    .single();
  if (error) throw error;
  return data;
}

export async function createRatioRule({
  organizationId,
  locationId,
  policySetId,
  label,
  minAgeMonths,
  maxAgeMonths,
  childrenPerQualifiedStaff,
  maxGroupSize,
}) {
  ensureLive();
  const { data, error } = await supabase
    .from("ratio_policy_rules")
    .insert({
      organization_id: organizationId,
      location_id: locationId,
      policy_set_id: policySetId,
      label: label.trim(),
      min_age_months: Number(minAgeMonths),
      max_age_months: Number(maxAgeMonths),
      children_per_qualified_staff: Number(childrenPerQualifiedStaff),
      max_group_size: maxGroupSize === "" || maxGroupSize == null ? null : Number(maxGroupSize),
    })
    .select("id, policy_set_id, location_id, label, min_age_months, max_age_months, children_per_qualified_staff, max_group_size")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteRatioRule(id) {
  ensureLive();
  const { error } = await supabase.from("ratio_policy_rules").delete().eq("id", id);
  if (error) throw error;
}

export async function setAssignmentRatioDesignation(id, countsTowardRatio) {
  ensureLive();
  const { data, error } = await supabase
    .from("staff_assignments")
    .update({ counts_toward_ratio: Boolean(countsTowardRatio) })
    .eq("id", id)
    .select("id, counts_toward_ratio")
    .single();
  if (error) throw error;
  return data;
}

export async function createChildScheduleWindow({
  organizationId,
  childId,
  classroomId,
  weekday,
  startsAt,
  endsAt,
  effectiveOn,
  endsOn,
  actorProfileId,
}) {
  ensureLive();
  const { data, error } = await supabase
    .from("child_schedule_windows")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      classroom_id: classroomId,
      weekday: Number(weekday),
      starts_at: startsAt,
      ends_at: endsAt,
      effective_on: effectiveOn,
      ends_on: endsOn || null,
      created_by: actorProfileId || null,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteChildScheduleWindow(id) {
  ensureLive();
  const { error } = await supabase.from("child_schedule_windows").delete().eq("id", id);
  if (error) throw error;
}

export async function createShiftBreak({
  organizationId,
  staffShiftId,
  startsAt,
  endsAt,
  actorProfileId,
}) {
  ensureLive();
  const { data, error } = await supabase
    .from("staff_shift_breaks")
    .insert({
      organization_id: organizationId,
      staff_shift_id: staffShiftId,
      starts_at: startsAt,
      ends_at: endsAt,
      created_by: actorProfileId || null,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteShiftBreak(id) {
  ensureLive();
  const { error } = await supabase.from("staff_shift_breaks").delete().eq("id", id);
  if (error) throw error;
}

export const WEEKDAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];
